import { ArrowLeft, PanelRightClose } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import { McpAppPaneFrame } from "~/pages/manifold-nextgen/components/conversations/McpAppPaneFrame";
import { useMcpAppsPaneState } from "~/pages/manifold-nextgen/components/conversations/useMcpAppsPane";
import { SessionContextDetailHeader } from "./SessionContextDetailHeader";
import type { SessionContextViewOption } from "./SessionContextDetailHeader";

interface SessionContextAppsDetailProps {
  // Human-readable label for this app group (e.g. "Cohorts" for
  // cohort_explorer), shown as the breadcrumb entity name.
  groupLabel: string;
  onBack: () => void;
  onCollapse: () => void;
}

/**
 * Shows the open app instances for one MCP-server group (e.g. all open
 * cohorts) as a single detail view: a breadcrumb + dropdown to switch between
 * them, mirroring `SessionContextAgentDetail`'s view-switcher. Every open
 * instance's iframe stays mounted and hidden rather than closed, so switching
 * is instant and doesn't lose the app's in-progress state.
 */
export const SessionContextAppsDetail = ({
  groupLabel,
  onBack,
  onCollapse,
}: SessionContextAppsDetailProps) => {
  const { tabs, activeKey, setActive, applyLocalUpdate } = useMcpAppsPaneState();
  const active = tabs.find((t) => t.key === activeKey) ?? tabs[tabs.length - 1] ?? null;

  const views: SessionContextViewOption[] = tabs.map((tab) => ({
    value: tab.key,
    label: tab.title,
  }));

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {active ? (
        <SessionContextDetailHeader
          entityName={groupLabel}
          onBack={onBack}
          onCollapse={onCollapse}
          views={views}
          currentView={active.key}
          onSetView={setActive}
        />
      ) : (
        <div className="flex h-14 shrink-0 items-center gap-1 border-b border-border px-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            aria-label="Back to session context"
            className="size-8"
          >
            <ArrowLeft className="size-4" />
          </Button>
          <span className="truncate text-sm font-medium text-muted-foreground">{groupLabel}</span>
          <Tooltip delayDuration={700}>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onCollapse}
                aria-label="Collapse session context"
                className="ml-auto size-8"
              >
                <PanelRightClose className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Toggle Context</TooltipContent>
          </Tooltip>
        </div>
      )}

      {active?.loading && (
        <div className="h-0.5 w-full overflow-hidden" role="progressbar" aria-label="Loading">
          <div className="h-full w-full origin-left bg-primary animate-indeterminate-bar" />
        </div>
      )}

      <div className="relative flex-1 overflow-hidden">
        {tabs.length === 0 ? (
          <div className="flex h-full items-center justify-center p-4 text-center text-xs text-muted-foreground">
            No {groupLabel.toLowerCase()} open.
          </div>
        ) : (
          // Keep every open instance's iframe mounted and toggle visibility, so
          // switching is instant instead of remounting and re-initializing.
          tabs.map((tab) => (
            <div
              key={tab.key}
              className={cn("absolute inset-0", tab.key === active?.key ? "block" : "hidden")}
            >
              {tab.html === null && tab.url === null ? (
                <div className="p-4 text-center text-xs text-destructive">
                  Couldn&apos;t load this panel. Try again shortly.
                </div>
              ) : tab.html !== undefined || tab.url !== undefined ? (
                <McpAppPaneFrame
                  html={tab.html ?? null}
                  url={tab.url ?? null}
                  title={tab.title}
                  serverId={tab.serverId}
                  state={tab.state}
                  onLocalUpdate={(s) => applyLocalUpdate(tab.key, s)}
                />
              ) : (
                <div className="p-4 text-xs text-muted-foreground">Loading panel…</div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
